import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';

import {
  Leaderboard,
  LeaderboardDivisionResults,
  LeaderboardFiltersState,
  LeaderboardOverviewResults,
  LeaderboardResultItem,
} from './_types';
import {
  denormalizeLeaderboard,
  normalizeLeaderboard,
  normalizeLeaderboardList,
  normalizeLeaderboardOverviewResults,
  normalizeResultItem,
} from './normalizers';

interface FetchLeaderboardsOptions {
  page?: number;
  perPage?: number;
  search?: string;
  sortBy?: string;
  sortDir?: string;
  visible?: boolean;
}

interface FetchLeaderboardsResponse {
  leaderboard: Leaderboard[];
  meta: {
    total: number;
  };
}

export const fetchLeaderboardsQueryKey = (params: FetchLeaderboardsOptions) => {
  return ['leaderboards', params];
};

export const fetchLeaderboards = async ({
  page = 1,
  perPage = 20,
  search,
  sortBy,
  sortDir,
  visible,
}: FetchLeaderboardsOptions = {}): Promise<FetchLeaderboardsResponse> => {
  const response = await api.get('/api/v4/leaderboards', {
    params: {
      page,
      per: perPage,
      'q[name_cont]': search,
      'q[s]': sortBy
        ? `${sortBy === 'createdAt' ? 'created_at' : sortBy} ${sortDir}`
        : undefined,
      ...(visible !== undefined && { 'q[visible]': visible ? 1 : 0 }),
    },
  });

  return normalizeLeaderboardList(response.data);
};

export const fetchLeaderboard = async (
  id: Identifier
): Promise<Leaderboard> => {
  const response = await api.get(`/api/v4/leaderboards/${id}`);
  return normalizeLeaderboard(response.data);
};

export const createLeaderboard = async (
  leaderboard: Partial<Leaderboard>
): Promise<Leaderboard> => {
  const response = await api.post(
    '/api/v4/leaderboards',
    denormalizeLeaderboard(leaderboard)
  );
  return normalizeLeaderboard(response.data);
};

export const updateLeaderboard = async (
  id: Identifier,
  leaderboard: Partial<Leaderboard>
): Promise<Leaderboard> => {
  const response = await api.put(
    `/api/v4/leaderboards/${id}`,
    denormalizeLeaderboard(leaderboard)
  );
  return normalizeLeaderboard(response.data);
};

export const deleteLeaderboard = async (id: Identifier): Promise<void> => {
  await api.delete(`/api/v4/leaderboards/${id}`);
};

// --- Public leaderboard API calls ---

export const fetchMeLeaderboardsOverview = async (): Promise<
  (LeaderboardResultItem | null)[]
> => {
  const response = await api.get(
    '/api/v4/leaderboards/me_leaderboards_overview'
  );
  return (response.data || []).map((item: any) =>
    item ? normalizeResultItem(item) : null
  );
};

interface FetchLeaderboardResultsParams extends LeaderboardFiltersState {
  type?: string;
  key?: string;
  subkey?: string;
  page?: number;
  per?: number;
}

export const fetchLeaderboardOverviewResults = async (
  id: Identifier,
  filters: LeaderboardFiltersState = {}
): Promise<LeaderboardOverviewResults> => {
  const response = await api.get(`/api/v4/leaderboards/${id}/results`, {
    params: buildFilterParams({ page: 1, per: 10, ...filters }),
  });
  return normalizeLeaderboardOverviewResults(response.data);
};

export const fetchLeaderboardDivisionResults = async (
  id: Identifier,
  params: FetchLeaderboardResultsParams
): Promise<LeaderboardDivisionResults> => {
  const response = await api.get(`/api/v4/leaderboards/${id}/results`, {
    params: buildFilterParams(params),
  });
  const raw = response.data;
  return {
    results: (raw?.results || []).map((row: any) => normalizeResultItem(row)),
    meta: raw?.meta ?? { total: 0 },
  };
};

export const fetchLeaderboardSimilarResults = async (
  id: Identifier
): Promise<LeaderboardResultItem[]> => {
  const response = await api.get(
    `/api/v4/leaderboards/${id}/similar_results`
  );
  return (response.data || []).map((row: any) => normalizeResultItem(row));
};

interface FetchPlanWorkoutResultsParams extends LeaderboardFiltersState {
  planWorkoutId: Identifier;
  type?: string;
  key?: string;
  subkey?: string;
  page?: number;
  per?: number;
}

export const fetchPlanWorkoutOverviewResults = async (
  params: FetchPlanWorkoutResultsParams
): Promise<LeaderboardOverviewResults> => {
  const response = await api.get(
    '/api/v4/leaderboards/plan_workout_results',
    { params: buildPlanWorkoutFilterParams(params) }
  );
  return normalizeLeaderboardOverviewResults(response.data);
};

export const fetchPlanWorkoutDivisionResults = async (
  params: FetchPlanWorkoutResultsParams
): Promise<LeaderboardDivisionResults> => {
  const response = await api.get(
    '/api/v4/leaderboards/plan_workout_results',
    { params: buildPlanWorkoutFilterParams(params) }
  );
  const raw = response.data;
  return {
    results: (raw?.results || []).map((row: any) => normalizeResultItem(row)),
    meta: raw?.meta ?? { total: 0 },
  };
};

interface FetchPlanWorkoutSimilarResultsParams {
  planWorkoutId: Identifier;
  planExerciseId?: Identifier;
  planBlockId?: Identifier;
  key?: string;
  subkey?: string;
}

export const fetchPlanWorkoutSimilarResults = async (
  params: FetchPlanWorkoutSimilarResultsParams
): Promise<LeaderboardResultItem[]> => {
  const response = await api.get(
    '/api/v4/leaderboards/plan_workout_similar_results',
    {
      params: {
        plan_workout_id: params.planWorkoutId,
        plan_exercise_id: params.planExerciseId || '',
        plan_block_id: params.planBlockId || '',
        key: params.key,
        subkey: params.subkey,
      },
    }
  );
  return (response.data || []).map((row: any) => normalizeResultItem(row));
};

export const fetchWorkoutPlansWithLeaderboards = async (): Promise<any[]> => {
  const response = await api.get('/api/v3/workout-plans', {
    params: {
      group_calendars: true,
      'q[has_leaderboard]': true,
      for_user: true,
    },
  });
  return response.data;
};

export const fetchPlanWorkoutsForCalendar = async (params: {
  planId: Identifier;
  startDate?: number;
  endDate?: number;
  page?: number;
  perPage?: number;
}): Promise<{ planWorkouts: any[]; meta: { total: number } }> => {
  const response = await api.get('/api/v2/plan_workouts', {
    params: {
      plan_id: params.planId,
      start_date: params.startDate,
      end_date: params.endDate,
      page: params.page || 1,
      per: params.perPage || 15,
      'q[s]': 'calendar_date desc',
    },
  });
  return {
    planWorkouts: response.data?.plan_workout || response.data || [],
    meta: response.data?.meta || { total: 0 },
  };
};

export const fetchPlanWorkoutForLeaderboard = async (
  id: Identifier
): Promise<any> => {
  const response = await api.get(`/api/v2/plan_workouts/${id}`);
  return response.data;
};

export const fetchPlanWorkoutsByDate = async (
  planId: Identifier,
  date: number
): Promise<any[]> => {
  const response = await api.get('/api/v2/plan_workouts', {
    params: {
      plan_id: planId,
      start_date: date,
      end_date: date,
    },
  });
  return response.data?.plan_workout || response.data || [];
};

export const fetchLoggedWorkoutsForPlan = async (
  planId: Identifier
): Promise<any[]> => {
  const response = await api.get('/api/v2/workouts', {
    params: {
      for_plan: planId,
      fetch_all: true,
    },
  });
  return response.data?.workout || response.data || [];
};

function buildFilterParams(params: FetchLeaderboardResultsParams) {
  return {
    type: params.type,
    key: params.key,
    subkey: params.subkey,
    page: params.page,
    per: params.per,
    location_id: params.locationId || undefined,
    location_ids: params.locationIds?.length
      ? params.locationIds
      : undefined,
    location_tags: params.locationTags?.length
      ? params.locationTags
      : undefined,
    age_min: params.ageMin || undefined,
    age_max: params.ageMax || undefined,
    gender: params.gender || undefined,
  };
}

function buildPlanWorkoutFilterParams(
  params: FetchPlanWorkoutResultsParams
) {
  return {
    plan_workout_id: params.planWorkoutId,
    type: params.type,
    key: params.key,
    subkey: params.subkey,
    page: params.page,
    per: params.per,
    location_id: params.locationId || undefined,
    location_ids: params.locationIds?.length
      ? params.locationIds
      : undefined,
    location_tags: params.locationTags?.length
      ? params.locationTags
      : undefined,
    age_min: params.ageMin || undefined,
    age_max: params.ageMax || undefined,
    gender: params.gender || undefined,
  };
}
